import React, { useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import AuthContext from "../auth/AuthContext";
import PostRenderer from "../post/PostRenderer";
import GroundingExcerpt from "./grounding/GroundingExcerpt";
import { normalizeVisibility } from "../post/compositions";
import { getPostText } from "../utils/postContent";

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function postPath(post) {
  if (post?.post_type === "recipe" || post?.recipe_uuid) {
    // Signed-in users with a preference open their preferred variant from list cards.
    const preferred = post.preferred_permalink;
    return `/recipes/${preferred || post.permalink || post.recipe_uuid}`;
  }
  return `/posts/${post.permalink || post.post_uuid}`;
}

const VISIBILITY_CHIP = {
  private: { label: "Privat", color: "default" },
  users: { label: "Brugere", color: "info" },
  public: { label: "Offentlig", color: "success" },
};

function Post({ post, variant = "card", linkTo, onEdit }) {
  const { user } = useContext(AuthContext);
  if (!post) return null;

  const visibility = normalizeVisibility(post.visibility);
  const publishedLabel = formatDate(post.published_at);
  const createdLabel = formatDate(post.created_at);
  const href = linkTo || postPath(post);
  const isGrounding = post.composition === "grounding";
  const excerpt = post.excerpt || getPostText(post.body).slice(0, 200);
  const chip = VISIBILITY_CHIP[visibility] || VISIBILITY_CHIP.private;
  const authorName = post.author?.name;
  const tags = Array.isArray(post.tags) ? post.tags : [];
  const commentCount = Number(post.comment_count) || 0;
  const showCommentCount = Boolean(user) && commentCount > 0;
  const variantCount = Number(post.variant_count) || 0;
  const isRecipe = post.post_type === "recipe" || Boolean(post.recipe_uuid);
  const showVariantsLink = isRecipe && variantCount > 0 && !post.parent_recipe_uuid;
  const rootRefForVariants = post.permalink || post.recipe_uuid || post.root_recipe_uuid;

  if (variant === "detail") {
    return <PostRenderer post={post} onEdit={onEdit} />;
  }

  return (
    <Box
      component="article"
      sx={{
        py: 2.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
        {visibility !== "public" ? (
          <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />
        ) : null}
        {post.variant_number ? (
          <Chip size="small" label={`v${post.variant_number}`} variant="outlined" />
        ) : null}
        <Typography variant="caption" color="text.secondary">
          {createdLabel || publishedLabel || ""}
          {authorName ? (
            <>
              {" · "}
              <Typography
                component={RouterLink}
                to={
                  isRecipe
                    ? `/recipes?author=${encodeURIComponent(authorName)}`
                    : `/?author=${encodeURIComponent(authorName)}`
                }
                variant="caption"
                sx={{ color: "inherit", textDecoration: "none", "&:hover": { color: "primary.main" } }}
              >
                {authorName}
              </Typography>
            </>
          ) : null}
          {showCommentCount ? (
            <>
              {" · "}
              {commentCount} {commentCount === 1 ? "kommentar" : "kommentarer"}
            </>
          ) : null}
        </Typography>
      </Box>
      <Typography
        component={RouterLink}
        to={href}
        variant="h5"
        sx={{
          fontWeight: 700,
          letterSpacing: "-0.02em",
          textDecoration: "none",
          color: "text.primary",
          "&:hover": { color: "primary.main" },
        }}
      >
        {post.title}
      </Typography>
      {isGrounding ? (
        <GroundingExcerpt body={post.body} excerpt={post.excerpt} />
      ) : excerpt ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.65 }}>
          {excerpt}
        </Typography>
      ) : null}
      {showVariantsLink ? (
        <Typography
          component={RouterLink}
          to={`/recipes?root=${encodeURIComponent(rootRefForVariants)}`}
          variant="body2"
          sx={{ mt: 1, display: "inline-block", color: "primary.main", textDecoration: "none" }}
        >
          {variantCount} {variantCount === 1 ? "variant" : "varianter"}
        </Typography>
      ) : null}
      {tags.length ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
          {tags.map((t) => (
            <Chip
              key={t}
              component={RouterLink}
              to={`/categories?tag=${encodeURIComponent(t)}`}
              clickable
              size="small"
              label={t}
              variant="outlined"
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

export default Post;
